import { create } from 'zustand'

import { ReferralCode } from '@src/types/referralcode'
import { IToken } from '@src/types/token'
import { Address } from '@src/types/token'

interface ApplicationState {
    exchangeId: string
    orderId: string
    currentModalId: string
    walletDrawerOpened: boolean
    faucetAddresses: Record<string, boolean>
    // shareable ref code of Bob that Bob has generated
    generatedReferralCode: ReferralCode
    // ref code of Bob that Alice has bound to her wallet
    boundReferralCode: ReferralCode
    waitingForConnectedWalletInteraction: boolean
    walletSelectedSellToken: IToken | undefined
    walletButtonDisplayChainId: number
    // Actions
    setExchangeId: (exchangeId: string) => void
    setOrderId: (orderId: string) => void
    setCurrentModalId: (modalId: string) => void
    openWalletDrawer: (isOpen: boolean) => void
    setFaucetAddresses: (data: { address: string; isFaucet: boolean }) => void
    setGeneratedReferralCode: (code: ReferralCode) => void
    setBoundReferralCode: (code: ReferralCode) => void
    setWaitingForConnectedWalletInteraction: (isWaiting: boolean) => void
    refreshStatus: () => void
    setWalletButtonDisplayChainId: (chainId: number) => void // Function to update the chain ID
    setWalletSelectedSellToken: (token: IToken | undefined) => void
}

export const useApplicationStore = create<ApplicationState>(set => ({
    // Initial state
    exchangeId: '',
    orderId: '',
    currentModalId: '',
    walletDrawerOpened: false,
    faucetAddresses: JSON.parse(localStorage.getItem('faucet') || '{}'),
    generatedReferralCode: {
        userAddress: '' as Address,
        code: '',
    },
    boundReferralCode: {
        userAddress: '' as Address,
        code: '',
    },
    waitingForConnectedWalletInteraction: false,
    walletButtonDisplayChainId: 0, // Initial state
    walletSelectedSellToken: undefined,

    // Actions
    setExchangeId: exchangeId => set({ exchangeId }),

    setOrderId: orderId => set({ orderId }),

    setCurrentModalId: modalId => set({ currentModalId: modalId }),

    openWalletDrawer: isOpen => set({ walletDrawerOpened: isOpen }),

    setFaucetAddresses: data =>
        set(state => {
            const newFaucetAddresses = {
                ...state.faucetAddresses,
                [data.address]: data.isFaucet,
            }
            localStorage.setItem('faucet', JSON.stringify(newFaucetAddresses))
            return { faucetAddresses: newFaucetAddresses }
        }),

    setGeneratedReferralCode: code => set({ generatedReferralCode: code }),

    setBoundReferralCode: code => set({ boundReferralCode: code }),

    setWaitingForConnectedWalletInteraction: isWaiting => set({ waitingForConnectedWalletInteraction: isWaiting }),

    setWalletSelectedSellToken: token => {
        set({ walletSelectedSellToken: token })
    },

    refreshStatus: () =>
        set({
            exchangeId: '',
            orderId: '',
            currentModalId: '',
        }),

    setWalletButtonDisplayChainId: chainId => set({ walletButtonDisplayChainId: chainId }),
}))
